import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Building2,
  Home,
  Users,
  Receipt,
  FileText,
  LogOut,
  Menu,
  X,
  UserCircle,
  AlertTriangle,
  KeyRound,
  Map,
  Gauge,
  Upload,
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/properties', icon: Building2, label: 'Immobilien' },
  { path: '/landlords', icon: UserCircle, label: 'Vermieter' },
  { path: '/units', icon: Home, label: 'Einheiten' },
  { path: '/tenants', icon: Users, label: 'Mieter' },
  { path: '/meters', icon: Gauge, label: 'Zähler' },
  { path: '/costs', icon: Receipt, label: 'Kosten' },
  { path: '/billings', icon: FileText, label: 'Abrechnungen' },
  { path: '/damages', icon: AlertTriangle, label: 'Schäden' },
  { path: '/mieter-zugaenge', icon: KeyRound, label: 'Mieterzugänge' },
  { path: '/import', icon: Upload, label: 'Import' },
  { path: '/roadmap', icon: Map, label: 'Roadmap' },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-button"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-md shadow-md"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out z-40 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <Building2 className="h-8 w-8 text-emerald-600" />
          <span className="ml-3 font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Kivera
          </span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ height: 'calc(100vh - 64px - 80px)' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              <item.icon size={20} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="text-sm">
                <p className="font-medium text-slate-900 truncate max-w-[120px]">{user?.name}</p>
                <p className="text-slate-500 text-xs truncate max-w-[120px]">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-900"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen flex flex-col">
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-6 flex-1">
          {children}
        </div>
        <footer className="px-4 md:px-8 py-6 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-2 justify-center lg:justify-start">
          <Link to="/impressum" className="hover:text-slate-900">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-slate-900">Datenschutz</Link>
          <Link to="/agb" className="hover:text-slate-900">AGB</Link>
          <Link to="/roadmap" className="hover:text-slate-900">Roadmap</Link>
          <span className="ml-auto hidden lg:inline">© {new Date().getFullYear()} Kivera</span>
        </footer>
      </main>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}
